import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, type LinkProps, useSearchParams } from 'react-router-dom';

import { cn } from '../../../lib';
import { type ButtonProps, buttonVariants } from '../button';
import { useGenerateLinkForPagination } from './useGenerateLinkForPagination';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />,
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
  LinkProps;

const PaginationLink = ({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) => (
  <Link
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'ghost',
        size,
      }),
      className,
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => {
  const { t } = useTranslation();
  return (
    <PaginationLink
      aria-label={t('pagination.previousAriaLabel')}
      size="default"
      className={cn('gap-1 pl-2.5', className)}
      {...props}
    >
      <ChevronLeft className="h-4 w-4" />
      <span>{t('pagination.previous')}</span>
    </PaginationLink>
  );
};
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>) => {
  const { t } = useTranslation();
  return (
    <PaginationLink
      aria-label={t('pagination.nextAriaLabel')}
      size="default"
      className={cn('gap-1 pr-2.5', className)}
      {...props}
    >
      <span>{t('pagination.next')}</span>
      <ChevronRight className="h-4 w-4" />
    </PaginationLink>
  );
};
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export const PaginationComponent = ({
  className,
  totalData,
  PAGE_SIZE = 10,
  isLoading,
  slug,
  ...props
}: React.ComponentProps<'nav'> & {
  totalData: number;
  PAGE_SIZE?: number;
  isLoading?: boolean;
  slug?: string;
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const generateLink = useGenerateLinkForPagination();

  const currentPage = React.useMemo(() => {
    const page = Number.parseInt(searchParams.get(slug ? `${slug}-page` : 'page') ?? '', 10);
    return Number.isNaN(page) || page < 1 ? 1 : page;
  }, [searchParams, slug]);
  const totalPages = React.useMemo(() => Math.ceil((totalData ?? 0) / PAGE_SIZE), [totalData]);
  const previousPages = React.useMemo(
    () => [2, 1].map(sub => currentPage - sub).filter(v => v > 0),
    [currentPage],
  );
  const nextPages = React.useMemo(
    () => [1, 2].map(sub => currentPage + sub).filter(v => v <= totalPages),
    [currentPage, totalPages],
  );

  return isLoading ? (
    <></>
  ) : (
    <div className="relative w-full">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center text-sm text-muted-foreground whitespace-nowrap pl-1">
        {t('pagination.results', { count: totalData })}
      </div>
      <Pagination className={className} {...props}>
        <PaginationContent>
          {currentPage > 1 && (
            <PaginationItem>
              <PaginationPrevious to={generateLink(currentPage - 1, slug)} />
            </PaginationItem>
          )}
          {previousPages.length > 0 && previousPages[0] > 1 && (
            <>
              <PaginationItem>
                <PaginationLink to={generateLink(1, slug)}>1</PaginationLink>
              </PaginationItem>
              {previousPages[0] !== 2 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
            </>
          )}
          {previousPages.map(page => (
            <PaginationItem key={page}>
              <PaginationLink to={generateLink(page, slug)}>{page}</PaginationLink>
            </PaginationItem>
          ))}
          <PaginationItem>
            <PaginationLink to={generateLink(currentPage, slug)} isActive>
              {currentPage}
            </PaginationLink>
          </PaginationItem>
          {nextPages.map(page => (
            <PaginationItem key={page}>
              <PaginationLink to={generateLink(page, slug)}>{page}</PaginationLink>
            </PaginationItem>
          ))}
          {nextPages.length > 0 && nextPages[nextPages.length - 1] < totalPages && (
            <>
              {nextPages[nextPages.length - 1] !== totalPages - 1 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink to={generateLink(totalPages, slug)}>{totalPages}</PaginationLink>
              </PaginationItem>
            </>
          )}
          {currentPage < totalPages && (
            <PaginationItem>
              <PaginationNext to={generateLink(currentPage + 1, slug)} />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
